<template>
    <div class="mx-2 mx-lg-5">
        <div class="mb-1 mt-3">
            <div class="d-flex align-items-center gap-3">
                <button
                    v-if="$store.getters.hasGatehousePerm('add_repeatingevent')"
                    class="btn btn-sm btn-primary px-3 py-2 d-flex align-items-center"
                    @click="addRepeatingEvent"
                >
                    <icon icon="Plus"></icon>
                    <span class="ms-2 fs-6 fw-bold">{{ $t("repeatingEvents.addRepeatingEvent") }}</span>
                </button>
                <button
                    v-if="$store.getters.hasGatehousePerm('add_vehicleentry')"
                    class="btn btn-sm btn-primary px-3 py-2 d-flex align-items-center"
                    @click="copyRecord"
                >
                    <icon icon="Copy"></icon>
                    <span class="ms-2 fs-6 fw-bold">{{ $t("utils.copy") }}</span>
                </button>
                <button
                    v-if="canEdit"
                    class="btn btn-primary btn-sm px-3 py-2 d-flex align-items-center"
                    @click="goToEdit"
                >
                    <icon icon="Edit"></icon>
                    <span class="ms-2 fs-6 fw-bold">{{ $t("utils.editDetails") }}</span>
                </button>
            </div>
        </div>
        <div class="row">
            <div class="col-12 col-md-6">
                <div class="card mt-3">
                    <div class="card-header">
                        {{ $t("headers.vehicleDetail") }}
                    </div>
                    <div class="card-body">
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{{ $t("vehicleEntry.carrier") }}</dt>
                            <dd class="col-sm-8">{{ vehicleEntry.carrier }}</dd>

                            <dt class="col-sm-4">{{ $t("vehicleEntry.driver_name") }}</dt>
                            <dd class="col-sm-8">{{ vehicleEntry.driver_name }}</dd>
                            <template v-if="vehicleEntry.driver_phone">
                                <dt class="col-sm-4">{{ $t("vehicleEntry.driverPhone") }}</dt>
                                <dd class="col-sm-8"><a :href="`tel:${vehicleEntry.driver_phone}`">{{ vehicleEntry.driver_phone }}</a></dd>
                            </template>

                            <dt class="col-sm-4">{{ $t("vehicleEntry.licencePlate") }}</dt>
                            <dd class="col-sm-8">{{ vehicleEntry.licence_plate }}</dd>

                            <dt class="col-sm-4">
                                <span v-if="vehicleEntry.actual_from">
                                    {{ $t("vehicleEntry.from") }}
                                </span>
                                <span v-else>{{ $t("vehicleEntry.expectedFrom") }}</span>
                            </dt>
                            <dd class="col-sm-8">
                                <span v-if="vehicleEntry.actual_from">
                                    {{ $d(new Date(vehicleEntry.actual_from), 'short') }}
                                </span>
                                <span v-else-if="vehicleEntry.expected_from">
                                    {{ $d(new Date(vehicleEntry.expected_from), 'short') }}
                                </span>
                                <span v-else>-</span>
                            </dd>

                            <dt class="col-sm-4">
                                <span v-if="vehicleEntry.actual_to">
                                    {{ $t("vehicleEntry.to") }}
                                </span>
                                <span v-else>{{ $t("vehicleEntry.expectedTo") }}</span>
                            </dt>
                            <dd class="col-sm-8">
                                <span v-if="vehicleEntry.actual_to">
                                    {{ $d(new Date(vehicleEntry.actual_to), 'short') }}
                                </span>
                                <span v-else-if="vehicleEntry.expected_to">
                                    {{ $d(new Date(vehicleEntry.expected_to), 'short') }}
                                </span>
                                <span v-else>—</span>
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-6 mt-3 mt-md-0">
                <div class="card mt-3">
                    <div class="card-header">
                        {{ $t("headers.vehicleInfo") }}
                    </div>
                    <div class="card-body">
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{{ $t("status.status") }}</dt>
                            <dd class="col-sm-8">
                                <div
                                    class="d-flex flex-column flex-lg-row justify-content-between align-items-center w-75">
                                    <div class="d-flex align-items-center mb-2 mb-lg-0">
                                        <icon v-if="vehicleEntry.status_icon" :icon="vehicleEntry.status_icon" class="status_icon me-1" />
                                        <span class="text-nowrap">
                                            {{ vehicleEntry.status_name ? $t("status.vehicleEntry." +
                                                vehicleEntry.status_name) : "-" }}
                                        </span>
                                    </div>

                                    <button
                                        v-if="$store.getters.hasGatehousePerm('change_status_vehicleentry') && vehicleEntry.status_name !== 'left'"
                                        class="btn btn-primary fw-bold p-2 ms-5 w-100 d-flex align-items-center justify-content-center"
                                        @click="openStatusModal"
                                    >
                                        <icon icon="Edit" class="me-1"></icon>
                                        <span>{{ $t("utils.change") }}</span>
                                    </button>
                                </div>
                            </dd>


                            <dt class="col-sm-4">{{ $t("vehicleEntry.note") }}</dt>
                            <dd class="col-sm-8">{{ vehicleEntry.note || '—' }}</dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        {{ $t("headers.commodity") }}
                    </div>
                    <div class="card-body p-0">
                        <div v-if="commodities.length > 0">
                            <table class="table table-striped table-hover table-responsive-md">
                                <thead>
                                    <tr>
                                        <th>{{ $t("commodity.name") }}</th>
                                        <th>{{ $t("commodity.quantity") }}</th>
                                        <th v-if="vehicleEntry.status_name === 'left'">{{ $t("commodity.end_quantity") }}</th>
                                        <th>{{ $t("commodity.note") }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="commodity in commodities" :key="commodity.id">
                                        <td>{{ commodity.name }}</td>
                                        <td>{{ commodity.quantity }} {{ commodity.unit_name }}</td>
                                        <th v-if="vehicleEntry.status_name === 'left'">{{ commodity.end_quantity }} {{ commodity.unit_name }}</th>
                                        <td>{{ commodity.note }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="text-center" v-else>
                            <h3 class="text-danger fw-bold py-3">{{ $t("commodity.warning") }}</h3>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row mt-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        {{ $t("vehicleEntry.vehiclePhotos") }}
                    </div>
                    <div class="card-body">
                        <div class="d-flex flex-wrap justify-content-center align-items-center gap-2 my-3">
                            <input type="file" ref="fileInput" style="display: none;" accept="image/png, image/jpg"
                                @change="addPhoto" />
                            <div class="d-flex flex-column align-items-center gap-3">
                                <button v-if="$store.getters.hasGatehousePerm('add_vehiclephoto')"
                                    class="btn btn-primary p-3" @click="$refs.fileInput.click()"
                                    style="width: 200px; height: 150px;">
                                    <icon icon="CameraPlus"></icon>
                                </button>
                                <div v-if="!photos.length" class="text-muted text-center fs-4 fw-bold">
                                    {{ $t("vehicleEntry.noPhoto") }}
                                </div>
                            </div>
                            <div class="d-flex flex-column align-items-center gap-2" v-for="photo in photos"
                                :key="photo.id" style="cursor: pointer; width: 200px;">
                                <img class="img_min" :src="getPhotoUrl(photo.id, true)" @click="openModal(photo.id)" />
                                <button v-if="$store.getters.hasGatehousePerm('delete_vehiclephoto')"
                                    class="btn btn-sm btn-danger" @click="deletePhoto(photo.id)">
                                    {{ $t("utils.delete") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <VehicleEntryStatusesModal :vehicleEntry="vehicleEntry" ref="statusModal" />

    <div class="modal fade" id="photoModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content modal_wrapper">
                <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                    <h3 class="modal-title">Fotky</h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-auto">
                    <button
                        class="btn btn-primary position-absolute top-50 start-0 translate-middle-y px-3 py-4 fs-4"
                        @click="prevPhoto"
                    >
                        <
                    </button>
                    <img :src="getPhotoUrl(currentPhotoId)" class="img-fluid" />
                    <button
                        class="btn btn-primary position-absolute top-50 end-0 translate-middle-y px-3 py-4 fs-4"
                        @click="nextPhoto"
                    >
                        >
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import VehicleEntryStatusesModal from '../components/VehicleEntryStatusesModal.vue';
import { CONFIG } from "@/config.js"
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";

export default {
    components: {
        Icon,
        VehicleEntryStatusesModal,
    },
    data() {
        return {
            vehicleEntry: {},
            commodities: [],
            photos: [],
            currentPhotoId: null,
            currentIndex: 0,
        }
    },
    created() {
        this.getVehicleEntry();
    },
    methods: {
        getVehicleEntry() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/vehicle-entries/${this.$route.params.id}`)
                .then(response => {
                    this.vehicleEntry = response.data;
                    this.getCommodities();
                    this.getPhotos();
                })
                .catch(error => {
                    console.error("Error loading vehicle entry", error);
                });
        },
        getCommodities() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/vehicle-entries/${this.vehicleEntry.id}/commodities`)
                .then(response => {
                    this.commodities = response.data;
                })
                .catch(error => {
                    console.error("Error loading commodities", error)
                });
        },
        goToEdit() {
            this.$router.push({
                name: "vehicleEntryEditView",
                params: {
                    slug: this.$route.params.slug,
                    id: this.vehicleEntry.id,
                }
            })
        },

        addPhoto(event) {
            const file = event.target.files[0];
            if (!file) return;

            const vehicleId = this.vehicleEntry.id;
            const fileName = `${vehicleId}_${file.name}`;

            const formData = new FormData();
            formData.append("file", file, fileName);

            this.$store.getters.api.post(`/${this.$route.params.slug}/vehicle-entries/${vehicleId}/photos/add`, formData)
                .then(() => {
                    this.getPhotos();
                })
                .catch(error => {
                    console.error("Error adding photo.", error);
                });
        },
        getPhotos() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/vehicle-entries/${this.vehicleEntry.id}/photos`)
                .then(response => {
                    this.photos = response.data;
                })
                .catch(error => {
                    console.error("Error loading photos.", error);
                });
        },
        getPhotoUrl(photoId, thumb = false) {
            var url = CONFIG.api.baseUrl;
            url += `/${this.$route.params.slug}/vehicle-entries/${this.vehicleEntry.id}/photos/${photoId}`
            if (thumb) {
                url += "?thumb=true";
            }
            return url
        },
        deletePhoto(photoId) {
            const confirm = window.confirm("Opravdu chcete smazat fotku?");
            if (!confirm) return;

            this.$store.getters.api.delete(`/${this.$route.params.slug}/vehicle-entries/${this.vehicleEntry.id}/photos/${photoId}/delete`)
                .then(() => {
                    this.getPhotos();
                })
                .catch(error => {
                    console.error("Error deleting photo.", error);
                });
        },

        openModal(photoId) {
            const index = this.photos.findIndex(p => p.id === photoId);
            if (index !== -1) {
                this.currentIndex = index;
                this.currentPhotoId = photoId;

                const modal = new Modal(document.getElementById('photoModal'));
                modal.show();
            }
        },
        nextPhoto() {
            // if (this.photos.length === 0) return;
            this.currentIndex = (this.currentIndex + 1) % this.photos.length;
            this.currentPhotoId = this.photos[this.currentIndex].id;
        },
        prevPhoto() {
            // if (this.photos.length === 0) return;
            this.currentIndex = (this.currentIndex - 1 + this.photos.length) % this.photos.length;
            this.currentPhotoId = this.photos[this.currentIndex].id;
        },
        copyRecord() {
            if (!this.$store.getters.hasGatehousePerm('add_vehicleentry')) return;

            this.$store.getters.api.get(`/${this.$route.params.slug}/statuses`, {
                params: {model: "vehicleentry"}
            })
            .then(res => {
                const statuses = res.data;
                const reservedStatus = statuses.find(s => s.name === "reserved");

                this.$store.commit("setCopyRecord", {
                    vehicle: {
                        ...this.vehicleEntry,
                        status: reservedStatus?.id
                    },
                    commodities: this.commodities,
                })
                this.$router.push({
                    name: "vehicleEntryAddView",
                    params: {
                        slug: this.$route.params.slug
                    }
                })
            })
            .catch(error => {
                console.error("Error copy record.", error);
            });


        },
        addRepeatingEvent() {
            this.$router.push({
                name: 'AddRepeatingEvent_VehicleEntry',
                params: {
                    slug: this.$route.params.slug,
                    id: this.vehicleEntry.id,
                }
            })
        },
        openStatusModal() {
            this.$nextTick(() => {
                this.$refs.statusModal.show();
            });
        }
    },
    computed: {
        canEdit() {
            const status = this.vehicleEntry.status_name;
            const canChange = this.$store.getters.hasGatehousePerm("change_vehicleentry");
            const canBeforeChange = this.$store.getters.hasGatehousePerm("before_change_vehicleentry");

            if (status === "left") {
                return canChange;
            }

            if (status === "reserved" || status === "arrived") {
                return canBeforeChange || canChange;
            }

            return false;
        },
    }
}
</script>
